import './App.css';
import { useState, useRef, useEffect, useMemo } from 'react';
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { appContext } from '../../../context/AppContext';
import {
	BadRequestError,
	InternalServerError,
	Merchant,
	MissingCredentialsError,
	Status,
	User,
	MerchantSettings,
	Country,
} from '@anticatrade/shared';
import { Spinner } from '@anticatrade/shared';
import {
	API,
	MerchantResponse,
	UnreadConversationsCountResponse,
	CredentialsResponse,
	LoginResponse,
	CountriesResponse,
} from '@anticatrade/admin';
import SlAlert from '@shoelace-style/shoelace/dist/react/alert/index.js';
import SlIcon from '@shoelace-style/shoelace/dist/react/icon/index.js';
import '@shoelace-style/shoelace/dist/themes/light.css';
import { setBasePath } from '@shoelace-style/shoelace/dist/utilities/base-path.js';
import * as icons from '../../../constants/icons';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n/i18n';
import { AppError } from '../../base/AppError/AppError';
import * as Sentry from '@sentry/react';
import { analytics } from '../../../analytics';

setBasePath('https://cdn.jsdelivr.net/npm/@shoelace-style/shoelace@2.20.1/cdn/');

function App() {
	const [status, setStatus] = useState<Status | null>(null);
	const [user, setUser] = useState<User>();
	const [settings, setSettings] = useState<MerchantSettings>();
	const [merchant, setMerchant] = useState<Merchant>();
	const [unreadMessagesCount, setUnreadMessagesCount] = useState<number>();
	const [countries, setCountries] = useState<Country[]>();
	const [isInitializingAPI, setIsInitializingAPI] = useState<boolean>(true);
	const [isLoadingState, setIsLoadingState] = useState<boolean>(true);
	const [isCheckingAndFetching, setIsCheckingAndFetching] = useState<boolean>(true);
	const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);

	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams] = useSearchParams();

	const toastRef = useRef<any | null>(null);
	const toastOpenTimeout = useRef<any>();

	const { t } = useTranslation();

	const api = useMemo(() => {
		let api: API;
		const browserLanguage = navigator.language.startsWith('it-')
			? 'it'
			: navigator.language.startsWith('fr-')
				? 'fr'
				: 'en';
		if (user == null || settings == null) {
			// create a temporary API based on the browser preference
			// for logged-out requests.
			api = new API(import.meta.env.VITE_SERVER_URL, browserLanguage);
		} else {
			api = new API(import.meta.env.VITE_SERVER_URL, settings!.languageCode);
		}
		setIsInitializingAPI(false);
		return api;
	}, [user, settings]);

	useEffect(() => {
		const setScrollbarWidth = () => {
			const scrollbarWidth = computeScrollbarWidth();
			document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
		};

		setScrollbarWidth();
		window.addEventListener('resize', setScrollbarWidth);

		return () => window.removeEventListener('resize', setScrollbarWidth);
	}, []);

	useEffect(() => {
		const scrollbarWidth = computeScrollbarWidth();

		const updatePadding = () => {
			if (document.body.classList.contains('body--scrollbar-hidden')) return;

			const hasScrollbar =
				document.documentElement.scrollHeight > document.documentElement.clientHeight;

			if (!hasScrollbar && scrollbarWidth > 0) {
				document.body.style.paddingRight = `${scrollbarWidth}px`;
			} else {
				document.body.style.paddingRight = '';
			}
		};

		updatePadding(); // First execution

		window.addEventListener('resize', updatePadding);
		const observer = new ResizeObserver(updatePadding);
		observer.observe(document.documentElement);

		return () => {
			window.removeEventListener('resize', updatePadding);
			observer.disconnect();
		};
	}, []);

	useEffect(() => {
		if (location.hash) {
			return;
		}
		const body = document.querySelector('body');
		body?.scrollTo({
			top: 0,
		});
	}, [location.key]);

	useEffect(() => {
		if (user == null || settings == null) {
			// use the preference of the browser.
			const lang = navigator.language.startsWith('it-')
				? 'it'
				: navigator.language.startsWith('fr-')
					? 'fr'
					: 'en';
			i18n.changeLanguage(lang);
			document.documentElement.lang = lang;
		} else {
			i18n.changeLanguage(settings.languageCode);
			document.documentElement.lang = settings.languageCode;
		}
	}, [user, settings]);

	useEffect(() => {
		const checkAndFetch = async () => {
			let credentialRes: CredentialsResponse;
			try {
				credentialRes = await api.credentials();
			} catch (err) {
				setIsCheckingAndFetching(false);
				return;
			}

			const user = credentialRes.user;
			setMerchant(credentialRes.merchant);
			setSettings(credentialRes.settings);

			let unreadConversationsCountRes: UnreadConversationsCountResponse;
			try {
				unreadConversationsCountRes = await api.unreadConversationsCount();
			} catch (err) {
				setIsCheckingAndFetching(false);
				return;
			}
			setUnreadMessagesCount(unreadConversationsCountRes.count);

			let countriesRes: CountriesResponse;
			try {
				countriesRes = await api.countries();
			} catch (err) {
				setIsCheckingAndFetching(false);
				return;
			}
			setCountries(countriesRes.countries);

			setIsLoggedIn(true);
			setIsCheckingAndFetching(false);
			setUser(user);
			analytics.identify(credentialRes.merchant);
			Sentry.setUser({ id: String(user.id) });
			Sentry.setTag('merchant_id', String(credentialRes.merchant.id));
		};
		if (isInitializingAPI || !isLoadingState) {
			return;
		}

		setIsCheckingAndFetching(true);
		checkAndFetch();
	}, [isInitializingAPI, isLoadingState]);

	useEffect(() => {
		if (isCheckingAndFetching) {
			return;
		}

		const stopLoadingState = () => {
			setTimeout(() => {
				setIsLoadingState(false);
			}, 300);
		};

		const isLoginPath = location.pathname === '/';
		if (!isLoggedIn && !isLoginPath) {
			// redirect to login.
			redirect('');
			stopLoadingState();
			return;
		} else if (isLoggedIn && isLoginPath) {
			// redirect to the main route.
			const query = searchParams.toString();
			redirect(`products${query ? `?${query}` : ''}`);
			stopLoadingState();
			return;
		}

		stopLoadingState();
	}, [isCheckingAndFetching, isLoggedIn]);

	const refreshMerchant = async () => {
		let merchantRes: MerchantResponse;
		try {
			merchantRes = await api.merchant();
		} catch (err) {
			return;
		}
		setMerchant(merchantRes.merchant);
	};

	const incrementUnreadMessagesCount = () => {
		let count = 0;
		if (unreadMessagesCount != null) {
			count = unreadMessagesCount;
		}
		setUnreadMessagesCount(count + 1);
	};

	const decrementUnreadMessagesCount = () => {
		let count = 0;
		if (unreadMessagesCount != null) {
			count = unreadMessagesCount;
		}
		setUnreadMessagesCount(count - 1);
	};

	const redirect = (location: string) => {
		if (toastRef.current) {
			toastRef.current.hide();
		}
		return navigate(`/${location}`);
	};

	const login = async (email: string, password: string, captchaToken: string) => {
		let res: LoginResponse;
		try {
			res = await api.login(email, password, captchaToken);
		} catch (err) {
			handleError(err as Error);
			throw err;
		}
		if (res.error) {
			if (res.error === 'AuthenticationFailed') {
				handleError(t('Your email or password are not correct'));
				throw new Error(res.error);
			}
			throw new Error(res.error);
		}
		setIsLoggedIn(true);
		const user = res.user;
		setUser(user);
		Sentry.setUser({ id: String(user.id) });
		if (user.merchantID != null) {
			Sentry.setTag('merchant_id', String(user.merchantID));
		}
		setIsLoadingState(true);
		redirect('products');
	};

	const logout = async () => {
		try {
			await api.logout();
		} catch (err) {
			handleError(err as Error);
			return;
		}
		setIsLoggedIn(false);
		analytics.reset();
		Sentry.setUser(null);
		Sentry.setTag('merchant_id', '');
		redirect('');
		setUser(undefined);
		setSettings(undefined);
	};

	const handleError = (err: Error | string) => {
		if (toastOpenTimeout.current) {
			clearTimeout(toastOpenTimeout.current);
		}
		if (err instanceof MissingCredentialsError) {
			logout();
			return;
		}
		if (toastRef.current == null) {
			return;
		}
		toastRef.current.hide();
		toastOpenTimeout.current = setTimeout(() => {
			var text: string;
			if (err instanceof InternalServerError || err instanceof BadRequestError) {
				if (import.meta.env.MODE === 'production') {
					Sentry.withScope((scope) => {
						scope.setExtra('type', err.name);
						scope.setExtra('path', scrubURL(window.location.href));
						Sentry.captureException(err);
					});
				}
				text = `${t('Something went wrong')}. ${t(
					'Our team has been informed and will proceed to investigate and fix the issue',
				)}`;
				if (err instanceof BadRequestError) {
					const { message, cause } = err;
					console.error(
						`%c Anticatrade: ${message}${cause ? ' | Cause: ' + cause : ''}`,
						'background:#dc362e;color:#dcdcdc',
					);
				}
			} else {
				text = err instanceof Error ? err.message : err;
			}
			setStatus({
				variant: 'danger',
				icon: icons.EXCLAMATION,
				text: text,
			});
			toastRef.current.toast();
			toastOpenTimeout.current = null;
		}, 300);
	};

	const showStatus = (status: Status) => {
		if (toastOpenTimeout.current) {
			clearTimeout(toastOpenTimeout.current);
		}
		if (toastRef.current == null) {
			return;
		}
		toastRef.current.hide();
		toastOpenTimeout.current = setTimeout(() => {
			setStatus(status);
			toastRef.current.toast();
			toastOpenTimeout.current = null;
		}, 300);
	};

	const app = (
		<appContext.Provider
			value={
				isLoadingState
					? undefined
					: {
							api: api,
							setIsLoadingState: setIsLoadingState,
							handleError: handleError,
							showStatus: showStatus,
							toastRef: toastRef,
							login: login,
							logout: logout,
							redirect: redirect,
							user: user,
							settings: settings,
							merchant: merchant,
							refreshMerchant: refreshMerchant,
							unreadMessagesCount: unreadMessagesCount,
							countries: countries,
							incrementUnreadMessagesCount: incrementUnreadMessagesCount,
							decrementUnreadMessagesCount: decrementUnreadMessagesCount,
						}
			}
		>
			<div className={`app${isLoadingState ? ' app--loading' : ''}`}>
				<SlAlert
					className='app__toast'
					ref={toastRef}
					variant={status?.variant}
					duration={
						status?.variant === 'success' || status?.variant === 'primary'
							? 5000
							: Infinity
					}
					closable
				>
					<SlIcon slot='icon' name={status?.icon} />
					<span
						className='app__toast-message'
						dangerouslySetInnerHTML={{ __html: status?.text || '' }}
					/>
				</SlAlert>
				{isLoadingState ? (
					<div className='app__spinner'>
						<Spinner />
					</div>
				) : (
					<Outlet />
				)}
			</div>
		</appContext.Provider>
	);

	if (import.meta.env.MODE === 'production') {
		return <AppError className='app__error-root'>{app}</AppError>;
	} else {
		return app;
	}
}

const computeScrollbarWidth = (): number => {
	const scrollDiv = document.createElement('div');
	scrollDiv.style.width = '100px';
	scrollDiv.style.height = '100px';
	scrollDiv.style.overflow = 'scroll';
	scrollDiv.style.position = 'absolute';
	scrollDiv.style.top = '-9999px';

	document.body.appendChild(scrollDiv);
	const scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;
	document.body.removeChild(scrollDiv);

	return scrollbarWidth;
};

function scrubURL(url: string): string {
	const sensitivePaths: Array<{ pattern: RegExp; placeholder: string }> = [];
	for (const { pattern, placeholder } of sensitivePaths) {
		if (pattern.test(url)) {
			return url.replace(pattern, placeholder);
		}
	}
	return url;
}

export { App, scrubURL };
